import React, { useMemo, useRef, useCallback, useEffect, useState, memo } from 'react';
// @ts-ignore
import MuxPlayer from '@mux/mux-player-react';
import ImagePlay from './play.png';
import { MUX_KEY, isDev } from '@constants';
import { useAnalytics, Events, useUser } from '@hooks';
import { logger } from '../../utils/logger';

// Type definitions for Mux Player (define locally to avoid import issues)
export type VideoOptions = Record<string, any>;

// Use your existing language names mapping (but we need uppercase keys to match menu display)
const LANGUAGE_NAMES: Record<string, string> = {
  'AR': 'Arabic',
  'BG': 'Bulgarian',
  'CS': 'Czech',
  'DA': 'Danish',
  'DE': 'German',
  'EL': 'Greek',
  'EN': 'English',
  'ES': 'Spanish',
  'ET': 'Estonian',
  'FI': 'Finnish',
  'FR': 'French',
  'HU': 'Hungarian',
  'ID': 'Indonesian',
  'IT': 'Italian',
  'JA': 'Japanese',
  'KO': 'Korean',
  'LT': 'Lithuanian',
  'LV': 'Latvian',
  'NB': 'Norwegian Bokmål',
  'NL': 'Dutch',
  'PL': 'Polish',
  'PT': 'Portuguese',
  'PT_BR': 'Brazilian Portuguese',
  'PT_PT': 'European Portuguese',
  'RO': 'Romanian',
  'RU': 'Russian',
  'SK': 'Slovak',
  'SL': 'Slovenian',
  'SV': 'Swedish',
  'TR': 'Turkish',
  'UK': 'Ukrainian',
  'ZH': 'Chinese',
  'ZH_HANS': 'Simplified Chinese',
  'ZH_HANT': 'Traditional Chinese',
  'LO': 'Lao',
  'TH': 'Thai',
  'TL': 'Tagalog',
  'HMN': 'Hmong',
  'VI': 'Vietnamese',
  'BN': 'Bengali',
  'HI': 'Hindi',
  'TE': 'Telugu',
  'PA': 'Punjabi',
  'TA': 'Tamil',
  'HE': 'Hebrew',
  'UR': 'Urdu',
  'FA': 'Persian (Farsi)',
  'SO': 'Somali',
  'AM': 'Amharic',
  'SW': 'Swahili',
  'HT': 'Haitian Creole',
  'NV': 'Navajo (Diné bizaad)'
};



type Props = {
  className?: string;
  options?: VideoOptions;
  metaData?: any;
  width?: number;
  height?: number;
  muted?: boolean;
  autoPlay?: boolean;
  src?: string | null;
  loop?: boolean;
  isVideoFile?: boolean;
  controls?: boolean;
  onStart?: () => void;
  onNext?: () => void;
  replay?: boolean;
  isProgram?: boolean;
  startTime?: number;
  firstPlay?: boolean;
  stopPlay?: boolean;
  isCoverVideo?: boolean;
  handleSetReplay?: () => void;
};

const VideoPlayer = ({
  className,
  options,
  metaData,
  width,
  height,
  src,
  muted,
  autoPlay,
  onStart,
  onNext,
  loop,
  isVideoFile,
  controls = true,
  replay,
  isProgram = false,
  startTime,
  firstPlay,
  stopPlay,
  isCoverVideo,
  handleSetReplay
}: Props) => {
  const playerRef = useRef<any>(null);
  const { trackEvent } = useAnalytics();
  const [user] = useUser();
  const [isPlaying, setIsPlaying] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const playbackId = useMemo(() => {
    if (src) {
      return /https:\/\/stream.mux.com\/(.*)\.m3u8/g.exec(src)?.[1] || undefined;
    }
    return undefined;
  }, [src]);

  const hlsConfig = useMemo(() => ({
    manifestLoadingTimeOut: 20000,
    levelLoadingTimeOut: 20000,
    manifestLoadingMaxRetry: 4,
    levelLoadingMaxRetry: 4,
    startLevel: -1, // Auto-select initial quality
    abrEwmaDefaultEstimate: 500000, // 500 kbps initial bandwidth estimate
    abrEwmaFastLive: 3.0,
    abrEwmaSlowLive: 9.0,
    abrEwmaFastVoD: 3.0,
    abrEwmaSlowVoD: 9.0,
    abrBandWidthFactor: 0.95, // Conservative bandwidth detection
    abrBandWidthUpFactor: 0.7, // Conservative upscaling
    abrMaxWithRealBitrate: true, // Use real bitrate for ABR decisions
    maxBufferLength: 30,
    maxMaxBufferLength: 600,
    backBufferLength: 90,
    maxBufferSize: 60 * 1000 * 1000,
    maxBufferHole: 0.5,
    highBufferWatchdogPeriod: 2,
    nudgeOffset: 0.2,
    nudgeMaxRetry: 5,
  }), []);

  const handleError = useCallback((e: Event) => {
    logger.error('Mux Player Error:', e);
    
    if (retryCount < 3) {
      logger.debug(`Attempting retry ${retryCount + 1}/3`);
      setTimeout(() => {
        setRetryCount(prev => prev + 1);
        if (playerRef.current) {
          playerRef.current.load();
        }
      }, 2000 * (retryCount + 1));
    } else {
      logger.error('Max retries exceeded');
    }
  }, [retryCount]);

  // Function to find all shadow roots in the document, optionally starting from a specific element
  const getAllShadowRoots = useCallback((root: Document | ShadowRoot | Element = document): ShadowRoot[] => {
    // If we're starting from an element, just get its shadow root if it has one
    if (root instanceof Element) {
      const shadowRoots: ShadowRoot[] = [];
      if (root.shadowRoot) {
        shadowRoots.push(root.shadowRoot);
        // Recursively search in the shadow root
        const elements = Array.from(root.shadowRoot.querySelectorAll('*'));
        for (const element of elements) {
          if (element instanceof Element && element.shadowRoot) {
            shadowRoots.push(element.shadowRoot);
            // Recursively search in shadow roots
            shadowRoots.push(...getAllShadowRoots(element.shadowRoot));
          }
        }
      }
      return shadowRoots;
    }
    
    // Otherwise, search all elements in the document or shadow root
    const elements = Array.from(root.querySelectorAll('*'));
    const shadowRoots: ShadowRoot[] = [];
    
    for (const element of elements) {
      if (element instanceof Element && element.shadowRoot) {
        shadowRoots.push(element.shadowRoot);
        // Recursively search in shadow roots
        shadowRoots.push(...getAllShadowRoots(element.shadowRoot));
      }
    }
    
    return shadowRoots;
  }, []);
  
  // Function to find elements in shadow roots, optionally limiting to a specific player type
  const findElementsInShadowDom = useCallback((selector: string): Element[] => {
    // If we're not in cover video mode, limit search to the content video player
    if (!isCoverVideo) {
      // Find the content video player element
      const contentPlayer = document.querySelector('[data-player-type="content-video"]');
      if (contentPlayer) {
        logger.info('Searching for elements in content-video player only');
        // Get shadow roots only within this player
        const shadowRoots = getAllShadowRoots(contentPlayer);
        const results: Element[] = [];
        
        for (const root of shadowRoots) {
          const elements = Array.from(root.querySelectorAll(selector));
          results.push(...elements);
        }
        
        return results;
      }
    }
    
    // Fallback to searching all shadow roots
    logger.info('Searching for elements in all shadow roots');
    const shadowRoots = getAllShadowRoots();
    const results: Element[] = [];
    
    for (const root of shadowRoots) {
      const elements = Array.from(root.querySelectorAll(selector));
      results.push(...elements);
    }
    
    return results;
  }, [getAllShadowRoots, isCoverVideo]);
  
  // Function to force captions off
  const forceCaptionsOff = useCallback(() => {
    if (!playerRef.current) return;
    
    // Set the player's textTrackMode to disabled
    try {
      // Use type assertion to access textTrackMode property
      (playerRef.current as any).textTrackMode = 'disabled';
    } catch (e) {
      logger.debug('Could not set textTrackMode property', e);
    }
    
    // Directly disable all text tracks
    const tracks = playerRef.current.textTracks;
    if (tracks) {
      for (let i = 0; i < tracks.length; i++) {
        tracks[i].mode = 'disabled';
      }
    }
    
    // Log the action
    logger.info('Forced captions off directly');
  }, []);
  
  // Function to handle the captions menu without simulated clicks
  const handleCaptionsMenu = useCallback(() => {
    try {
      // Force captions off directly
      forceCaptionsOff();
      
      // Skip menu manipulation for cover videos
      if (isCoverVideo) {
        logger.info('Skipping captions menu manipulation for cover video');
        return true;
      }
      
      // Find all media-captions-menu elements in the shadow DOM
      const captionsMenus = findElementsInShadowDom('media-captions-menu');
      
      if (captionsMenus.length === 0) {
        logger.info('No captions menus found in shadow DOM, will retry');
        return false;
      }
      
      logger.info(`Found ${captionsMenus.length} captions menus`);
      
      // Get the first menu
      const captionsMenu = captionsMenus[0];
      
      // Try different approaches to find menu items
      logger.info('Attempting to find menu items in captions menu');
      
      // First try: direct query for media-chrome-menu-item
      let menuItems: Element[] = Array.from(captionsMenu.querySelectorAll('media-chrome-menu-item'));
      
      // Second try: look in the shadow root if available
      if (menuItems.length === 0 && captionsMenu instanceof Element && captionsMenu.shadowRoot) {
        logger.info('Trying to find menu items in shadow root');
        menuItems = Array.from(captionsMenu.shadowRoot.querySelectorAll('media-chrome-menu-item'));
      }
      
      // Third try: look for any elements with role="menuitemradio"
      if (menuItems.length === 0) {
        logger.info('Trying to find menu items by role');
        menuItems = Array.from(captionsMenu.querySelectorAll('[role="menuitemradio"]'));
        
        // Also try in shadow root
        if (menuItems.length === 0 && captionsMenu instanceof Element && captionsMenu.shadowRoot) {
          menuItems = Array.from(captionsMenu.shadowRoot.querySelectorAll('[role="menuitemradio"]'));
        }
      }
      
      // Fourth try: look for any elements with type="radio"
      if (menuItems.length === 0) {
        logger.info('Trying to find menu items by type');
        menuItems = Array.from(captionsMenu.querySelectorAll('[type="radio"]'));
        
        // Also try in shadow root
        if (menuItems.length === 0 && captionsMenu instanceof Element && captionsMenu.shadowRoot) {
          menuItems = Array.from(captionsMenu.shadowRoot.querySelectorAll('[type="radio"]'));
        }
      }
      
      // Fifth try: look for any elements inside a slot
      if (menuItems.length === 0) {
        logger.info('Trying to find menu items in slots');
        const slots = Array.from(captionsMenu.querySelectorAll('slot'));
        for (const slot of slots) {
          if (slot instanceof HTMLSlotElement && slot.assignedNodes) {
            const assignedNodes = slot.assignedNodes();
            for (const node of assignedNodes) {
              if (node instanceof Element && node.querySelectorAll) {
                const items = Array.from(node.querySelectorAll('media-chrome-menu-item, [role="menuitemradio"], [type="radio"]'));
                menuItems.push(...items);
              }
            }
          }
        }
      }
      
      // Log what we found
      if (menuItems.length === 0) {
        logger.info('No menu items found using any method');
        if (captionsMenu instanceof Element) {
          logger.info('Captions menu HTML:', captionsMenu.outerHTML);
          
          // Try one more approach - get all elements and log them
          const allElements = Array.from(captionsMenu.querySelectorAll('*'));
          logger.info(`Found ${allElements.length} total elements in captions menu`);
          if (allElements.length > 0) {
            logger.info('First 5 elements:', allElements.slice(0, 5).map(el => 
              el instanceof Element ? el.outerHTML : 'Non-element node'
            ).join('\n'));
          }
        }
        
        // Try waiting for menu items to load dynamically with multiple retries
        logger.info('Menu items not found, will retry with multiple attempts');
        
        const retryFindMenuItems = (attemptNumber: number) => {
          const delay = attemptNumber * 500; // 500ms, 1000ms, 1500ms
          
          setTimeout(() => {
            logger.info(`Retry attempt ${attemptNumber}/3 after ${delay}ms delay`);
            
            // Try all approaches again after delay
            let retryMenuItems: Element[] = Array.from(captionsMenu.querySelectorAll('media-chrome-menu-item, [role="menuitemradio"], [type="radio"]'));
            
            if (retryMenuItems.length === 0 && captionsMenu instanceof Element && captionsMenu.shadowRoot) {
              retryMenuItems = Array.from(captionsMenu.shadowRoot.querySelectorAll('media-chrome-menu-item, [role="menuitemradio"], [type="radio"]'));
            }
            
            logger.info(`Found ${retryMenuItems.length} menu items after retry ${attemptNumber}`);
            
            if (retryMenuItems.length > 0) {
              // Process the found menu items
              logger.info('Successfully found menu items on retry, processing...');
              // Call the same processing logic as the main flow
              handleCaptionsMenu();
            } else if (attemptNumber < 3) {
              // Try again with next attempt
              retryFindMenuItems(attemptNumber + 1);
            } else {
              logger.warn('Failed to find menu items after all retry attempts');
            }
          }, delay);
        };
        
        // Start the retry sequence
        retryFindMenuItems(1);
        
        return false;
      }
      
      logger.info(`Found ${menuItems.length} menu items`);
      
      // Log current menu items content for debugging
      menuItems.forEach((item, index) => {
        const value = item.getAttribute('value');
        const textContent = item.textContent;
        logger.info(`Menu item ${index}: value="${value}", text="${textContent}"`);
      });
      
      // Find the "Off" item by its value attribute
      const offItem = menuItems.find(item => 
        item.getAttribute('value') === 'off'
      );
      
      // Find all items that are not "Off"
      const otherItems = menuItems.filter(item => 
        item.getAttribute('value') !== 'off'
      );
      
      if (offItem) {
        logger.info('Found Off item, setting it as active');
        
        // Set the "Off" item to checked
        offItem.setAttribute('aria-checked', 'true');
        
        // If the item has a classList, add the checked class
        if (offItem instanceof HTMLElement && offItem.classList) {
          offItem.classList.add('checked');
          offItem.setAttribute('part', 'menu-item radio checked');
        }
        
        // Set all other items to unchecked and update display text
        otherItems.forEach(item => {
          item.setAttribute('aria-checked', 'false');
          
          if (item instanceof HTMLElement && item.classList) {
            item.classList.remove('checked');
            item.setAttribute('part', 'menu-item radio');
          }
          
          // Map language codes to proper names using your existing mapping
          const menuValue = item.getAttribute('value');
          if (menuValue && menuValue.startsWith('sb:')) {
            // Parse the menu value format: "sb:languageCode:DISPLAY"
            const parts = menuValue.split(':');
            const displayCode = parts[2]; // e.g., "BG", "CS", "DA", "TL"
            const languageName = LANGUAGE_NAMES[displayCode];
            
            if (languageName) {
              // Extract the current display text (last line after CSS)
              const currentText = item.textContent?.trim();
              const textLines = currentText?.split('\n').filter(line => line.trim());
              const currentDisplayText = textLines?.[textLines.length - 1]?.trim();
              
              if (currentDisplayText && currentDisplayText === displayCode) {
                // Find and replace the text node
                const walkTextNodes = (node: Node) => {
                  if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim() === displayCode) {
                    node.textContent = languageName;
                    logger.info(`Updated menu item: "${displayCode}" -> "${languageName}"`);
                    return true;
                  }
                  for (const child of Array.from(node.childNodes)) {
                    if (walkTextNodes(child)) return true;
                  }
                  return false;
                };
                walkTextNodes(item);
              }
            }
          }
        });
        
        // Sort language items alphabetically
        otherItems.sort((a, b) => {
          const aText = a.textContent || '';
          const bText = b.textContent || '';
          return aText.trim().localeCompare(bText.trim());
        });
        
        // Get the parent element
        const parent = menuItems[0].parentElement;
        
        // Reattach items in the new order
        if (parent) {
          parent.appendChild(offItem); // First add "Off"
          otherItems.forEach(item => parent.appendChild(item)); // Then add sorted languages
          
          logger.info('Sorted captions menu items alphabetically');
        }
        
        // We've already set the "Off" option as active and all other options as inactive
        logger.info('Successfully set "Off" option as active and all other language options as inactive');
        
        return true;
      } else {
        logger.info('Could not find Off item');
        return false;
      }
    } catch (e) {
      logger.error('Error handling captions menu:', e);
      return false;
    }
  }, [findElementsInShadowDom, forceCaptionsOff, isCoverVideo]);

  useEffect(() => {
    setRetryCount(0);
  }, [src, playbackId]);

  useEffect(() => {
    if (startTime === 0 && playerRef?.current && autoPlay) {
      const isCurrentlyPlaying = playerRef?.current?.currentTime && playerRef?.current?.currentTime > 0 && !playerRef.current.paused && !playerRef.current.ended 
        && playerRef.current.readyState > playerRef.current.HAVE_CURRENT_DATA;
      if (isCurrentlyPlaying) {
        playerRef.current.pause();
      }
      playerRef.current.currentTime = 0;
      playerRef.current.muted = true;
      setTimeout(() => {
        if (playerRef?.current) {
          playerRef.current.play();
        }
      }, 10);
    }
  }, [startTime, autoPlay]);

  useEffect(() => {
    if (stopPlay && playerRef?.current) {
      const isCurrentlyPlaying = playerRef?.current?.currentTime && playerRef?.current?.currentTime > 0 && !playerRef.current.paused && !playerRef.current.ended 
        && playerRef.current.readyState > playerRef.current.HAVE_CURRENT_DATA;
      if (isCurrentlyPlaying) {
        playerRef.current.pause();
        setIsPlaying(false);
      }
      playerRef.current.currentTime = 0;
    }
  }, [stopPlay]);

  const handlePlayFromBegin = useCallback(() => {
    if (playerRef.current) {
      const isCurrentlyPlaying = playerRef?.current?.currentTime && playerRef?.current?.currentTime > 0 && !playerRef.current.paused && !playerRef.current.ended 
        && playerRef.current.readyState > playerRef.current.HAVE_CURRENT_DATA;
      if (isCurrentlyPlaying) {
        playerRef.current.pause();
      }
      playerRef.current.currentTime = 0;
      playerRef.current.play();
    }
  }, []);

  const handleTogglePlay = useCallback(() => {
    if (playerRef.current) {
      const isCurrentlyPlaying = playerRef?.current?.currentTime && playerRef?.current?.currentTime > 0 && !playerRef.current.paused && !playerRef.current.ended 
        && playerRef.current.readyState > playerRef.current.HAVE_CURRENT_DATA;
      if (!isCurrentlyPlaying) {
        playerRef.current.play();
      }
    }
  }, []);

  const handlePause = useCallback(() => {
    const isCurrentlyPlaying = playerRef?.current?.currentTime && playerRef?.current?.currentTime > 0 && !playerRef.current.paused && !playerRef.current.ended 
      && playerRef.current.readyState > playerRef.current.HAVE_CURRENT_DATA;
    if (playerRef.current && isCurrentlyPlaying) {
      setIsPlaying(false);
      playerRef.current.pause();
    }
  }, []);

  const handleEnd = useCallback(() => {
    setIsPlaying(false);
    if(onNext) {
      onNext();
    }
    if (!loop) {
      handleTogglePlay();
    }
  },[handleTogglePlay, onNext, loop]);

  const handleStart = useCallback(() => {
    const isCurrentlyPlaying = playerRef?.current?.currentTime && playerRef?.current?.currentTime > 0 && !playerRef.current.paused && !playerRef.current.ended 
      && playerRef.current.readyState > playerRef.current.HAVE_CURRENT_DATA;
    setIsPlaying(true);
    if (isCurrentlyPlaying) {
      return;
    }
    if (onStart) {
      onStart();
    }
    handleSetReplay?.();
    if (!isCoverVideo && !isPlaying) {
      trackEvent(Events.actions.videoWatched, {
        title: metaData?.name,
        playbackId,
        position: metaData?.position,
        url: metaData?.data,
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      });
    }
  }, [onStart, handleSetReplay, isCoverVideo, isPlaying, trackEvent, metaData?.name, metaData?.position, metaData?.data, playbackId, user]);

  useEffect(() => {
    if (replay) {
      handleTogglePlay();
    } else {
      handlePause();
    }
  }, [handleTogglePlay, handlePause, replay]);

  useEffect(() => {
    if (!muted && replay && firstPlay) {
      handlePlayFromBegin();
    }
  }, [handlePlayFromBegin, muted, replay, firstPlay]);

  const muxLogDisabled = useMemo(() => isDev || firstPlay || replay || startTime !== undefined || muted, [firstPlay, replay, startTime, muted]);
  const metaDataValue = useMemo(() => metaData ? ({...metaData}) : undefined, [metaData]);
  const videoClassName = useMemo(() => `video-js vjs-big-play-centered${className ? ` ${className}` : ''}`, [className]);

  return (
    <>
      <MuxPlayer
        {...(options || {})}
        key={`player-${retryCount}`}
        ref={playerRef}
        data-player-type={isCoverVideo ? "cover-video" : "content-video"}
        _hlsConfig={hlsConfig}
        playbackId={playbackId}
        streamType="on-demand"
        src={playbackId ? undefined : src || undefined}
        autoPlay={isCoverVideo ? false : autoPlay}
        muted={muted}
        className={videoClassName}
        envKey={muxLogDisabled ? undefined : MUX_KEY}
        metadata={{
          ...metaDataValue,
          playerName: 'Open Parachute Player',
          playerVersion: '1.0.0'
        }}
        onEnded={handleEnd}
        onPlay={handleStart}
        onPause={handlePause}
        onError={handleError}
        onWaiting={() => {
          logger.debug('Buffering...');
        }}
        onStalled={() => {
          logger.debug('Playback stalled, attempting to resume...');
          if (playerRef.current && !playerRef.current.paused) {
            playerRef.current.play().catch(console.error);
          }
        }}
        onSeeking={() => logger.debug('Seeking...')}
        onSeeked={() => logger.debug('Seek complete')}
        onLoadedMetadata={() => {
          logger.info('Player metadata loaded, disabling captions');
          forceCaptionsOff();
          if (!isCoverVideo) {
            logger.info('Setting up captions menu via onLoadedMetadata');
            handleCaptionsMenu();
          }
        }}
        loop={loop}
        startTime={startTime}
        defaultHiddenCaptions={true}
        defaultShowRemainingTime={true}
        theme="dark"
        disablePictureInPicture={muxLogDisabled}
        playsInline={true}
        maxResolution="2160p"
        nohotkeys={true}
        defaultDuration={0}
        accentColor="rgb(255, 176, 115)"
        style={{
          width: '100%',
          height: '100%',
          '--controls-backdrop-color': 'transparent',
          '--control-icons-active-color': 'rgb(255, 176, 115)',
          '--captions-button-color': 'white',
          '--captions-button-active-color': 'rgb(255, 176, 115)',
          '--captions-menu-background-color': 'rgba(28, 28, 28, 0.95)',
          '--captions-menu-item-color': 'white',
          '--captions-menu-item-active-color': 'rgb(255, 176, 115)',
          '--time-range-track-color': 'rgb(255, 176, 115)',
          '--time-range-value-color': 'rgb(255, 176, 115)',
          '--time-range-thumb-color': 'rgb(255, 176, 115)',
          '--volume-range-track-color': 'rgb(255, 176, 115)',
          '--volume-range-value-color': 'rgb(255, 176, 115)',
          '--volume-range-thumb-color': 'rgb(255, 176, 115)',
          // Hide all controls when controls prop is false
          ...(!controls && {
            '--controls': 'none',
            '--dialog': 'none',
            '--loading-indicator': 'none',
            '--play-button': 'none',
            '--live-button': 'none',
            '--seek-backward-button': 'none',
            '--seek-forward-button': 'none',
            '--mute-button': 'none',
            '--captions-button': 'none',
            '--airplay-button': 'none',
            '--pip-button': 'none',
            '--fullscreen-button': 'none',
            '--cast-button': 'none',
            '--playback-rate-button': 'none',
            '--volume-range': 'none',
            '--time-range': 'none',
            '--time-display': 'none',
            '--duration-display': 'none',
            '--rendition-menu-button': 'none',
            '--center-controls': 'none',
            '--bottom-play-button': 'none'
          })
        } as React.CSSProperties}
      />
      {(!isPlaying && isProgram && !controls) && (
        <div
          style={{
            position: 'absolute'
          }}
        >
          <img src={ImagePlay} alt='Play' onClick={handleTogglePlay} />
        </div>
      )}
    </>
  );
};

export default memo(VideoPlayer);
